import { computed, onMounted, onUnmounted, watch, WatchStopHandle } from "vue";

import useBackendClient from "@/client";
import {
  getPublicationStatusRef,
  IDLE_PUBLICATION_STATUS,
  PublicationStatus,
  setPublicationStatus,
} from "@/hooks/publicationStatus.store";

// Publications are rare and last a couple of minutes, so there is no idle polling:
// the status is fetched on mount, on window focus, and reflected instantly by the
// backend client when a mutation is rejected with the lock 409. Only while a
// publication is known to be in progress does a poll loop run — to track its end
// and clear the banner promptly.
const ACTIVE_POLL_INTERVAL_MS = 10_000;

interface StatusWatcher {
  subscribers: number;
  activeTimer: ReturnType<typeof setInterval> | null;
  stopWatching: WatchStopHandle;
  onFocus: () => void;
}

const watcherByAppId = new Map<string, StatusWatcher>();

/**
 * Publication (app lock) status for an app, from GET /app/:appId/version/publication-status.
 * State is shared per appId, so the banner, the publish buttons and the backend
 * client's 409 interceptor all see the same status through one watcher.
 */
export default function usePublicationStatus(appId: string) {
  const { client } = useBackendClient();
  const status = getPublicationStatusRef(appId);

  const refetch = async () => {
    try {
      const { data } = await client.get<PublicationStatus & { success: boolean }>(
        `/app/${appId}/version/publication-status`
      );
      setPublicationStatus(appId, {
        publicationInProgress: !!data?.publicationInProgress,
        startedBy: data?.startedBy ?? null,
        startedAt: data?.startedAt ?? null,
      });
    } catch (error) {
      // Endpoint not deployed yet (404) or unreachable: report idle rather than
      // stale "in progress" — the guard on the API stays the source of truth.
      setPublicationStatus(appId, { ...IDLE_PUBLICATION_STATUS });
    }
  };

  const ensureWatcher = () => {
    const existing = watcherByAppId.get(appId);
    if (existing) {
      existing.subscribers += 1;
      return;
    }

    const watcher: StatusWatcher = {
      subscribers: 1,
      activeTimer: null,
      onFocus: () => refetch(),
      stopWatching: watch(
        () => status.value.publicationInProgress,
        (inProgress) => {
          if (inProgress && !watcher.activeTimer) {
            watcher.activeTimer = setInterval(refetch, ACTIVE_POLL_INTERVAL_MS);
          } else if (!inProgress && watcher.activeTimer) {
            clearInterval(watcher.activeTimer);
            watcher.activeTimer = null;
          }
        },
        { immediate: true }
      ),
    };

    window.addEventListener("focus", watcher.onFocus);
    watcherByAppId.set(appId, watcher);
  };

  const releaseWatcher = () => {
    const watcher = watcherByAppId.get(appId);
    if (!watcher) {
      return;
    }
    watcher.subscribers -= 1;
    if (watcher.subscribers <= 0) {
      watcher.stopWatching();
      if (watcher.activeTimer) {
        clearInterval(watcher.activeTimer);
      }
      window.removeEventListener("focus", watcher.onFocus);
      watcherByAppId.delete(appId);
    }
  };

  onMounted(() => {
    refetch();
    ensureWatcher();
  });

  onUnmounted(releaseWatcher);

  return {
    publicationInProgress: computed(() => status.value.publicationInProgress),
    startedBy: computed(() => status.value.startedBy),
    startedAt: computed(() => status.value.startedAt),
    refetch,
  };
}
